import type { DocumentPipelineFilter } from '@kanbu/schema/contracts';
import { Role, type SourceType } from '@kanbu/schema/enums';
import type { PopulateParams } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { Dialog } from '@utima/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BreadCrumbItem } from '@/components/breadCrumb/BreadCrumb';
import { ResourcePage } from '@/components/page/ResourcePage';
import { Protected } from '@/components/protected/Protected';
import {
  RetryFailedButton,
  AddDocumentsModal,
  GenerateTestCasesDialog,
} from '@/features/documents';
import { DocumentExportModal } from '@/features/documents/components/DocumentExportModal';
import { AccessPropagationBanner } from '@/features/knowledge-base/components/AccessPropagationBanner';
import { BulkAgentAccessModal } from '@/features/knowledge-base/components/BulkAgentAccessModal';
import { BulkAutoUpdateModal } from '@/features/knowledge-base/components/BulkAutoUpdateModal';
import { BulkWeightModal } from '@/features/knowledge-base/components/BulkWeightModal';
import { DeleteFoldersModal } from '@/features/knowledge-base/components/DeleteFoldersModal';
import { KBContentsSection } from '@/features/knowledge-base/components/KBContentsSection';
import { KBContentTable } from '@/features/knowledge-base/components/KBContentTable';
import { KBStatsSummary } from '@/features/knowledge-base/components/KBStatsSummary';
import { KBToolbar } from '@/features/knowledge-base/components/KBToolbar';
import { useKBContents } from '@/features/knowledge-base/hooks/useKBContents';
import { useKBMutations } from '@/features/knowledge-base/hooks/useKBMutations';
import { useKBSelection } from '@/features/knowledge-base/hooks/useKBSelection';
import type { KBWebsitePageFilter } from '@/features/knowledge-base/lib/pageFilter';
import { trpc } from '@/services/trpc';

const populate: PopulateParams = ['organization.id'];

export const Route = createFileRoute('/_baseLayout/chats/$chatId/documents/')({
  component: DocumentsPage,
  loader: async ({ context: { trpcUtils }, params: { chatId } }) =>
    trpcUtils.chats.findOne.ensureData({ id: chatId, populate }),
  validateSearch: (
    search: { folderId?: string; open?: string } & SearchSchemaInput,
  ) => ({
    ...(search?.open ? { open: search.open } : {}),
  }),
});

export function DocumentsPage() {
  const { chatId } = Route.useParams();
  const searchParams = Route.useSearch();
  const { t } = useLingui();
  const navigate = useNavigate();
  const selection = useKBSelection();
  const mutations = useKBMutations();

  const [data] = trpc.chats.findOne.useSuspenseQuery({
    id: chatId,
    populate,
  });

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);

    if (params.has('folderId')) {
      navigate({
        to: '/chats/$chatId/documents',
        params: { chatId },
        search: searchParams?.open ? { open: searchParams.open } : {},
        replace: true,
      });
    }
  }, [chatId, navigate, searchParams?.open]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    KBWebsitePageFilter | undefined
  >();
  const [documentPipelineFilter, setDocumentPipelineFilter] = useState<
    DocumentPipelineFilter | undefined
  >();
  const [sourceTypeFilter, setSourceTypeFilter] = useState<
    SourceType | undefined
  >();
  const [showAddDocuments, setShowAddDocuments] = useState(
    !!searchParams?.open,
  );

  const handleStatusFilterChange = useCallback(
    (status: KBWebsitePageFilter | undefined) => {
      setDocumentPipelineFilter(undefined);
      setStatusFilter(prev =>
        status !== undefined && status === prev ? undefined : status,
      );
    },
    [],
  );

  const handleDocumentPipelineFilterChange = useCallback(
    (filter: DocumentPipelineFilter | undefined) => {
      setStatusFilter(undefined);
      setDocumentPipelineFilter(prev =>
        filter !== undefined && filter === prev ? undefined : filter,
      );
    },
    [],
  );

  // Source lens (master plan §1A): orthogonal to status/documentPipelineFilter
  // — it narrows by origin, not processing state, so it composes with either
  // instead of resetting them.
  const handleSourceTypeFilterChange = useCallback(
    (sourceType: SourceType | undefined) => {
      setSourceTypeFilter(prev =>
        sourceType !== undefined && sourceType === prev
          ? undefined
          : sourceType,
      );
    },
    [],
  );

  const contents = useKBContents({
    folderId: null,
    chatId,
    search,
    statusFilter,
    documentPipelineFilter,
    sourceTypeFilter,
    selection,
    mutations,
  });

  const { data: docStats } = trpc.documents.getStats.useQuery(
    { chatId },
    {
      refetchOnMount: 'always',
      refetchInterval: query => {
        const stats = query.state.data;

        return stats &&
          stats.uploading +
            stats.ready +
            stats.parsing +
            stats.parsingAsync +
            stats.parsed +
            stats.embedding >
            0
          ? 5000
          : false;
      },
    },
  );

  const [allChats] = trpc.chats.findAll.useSuspenseQuery({
    where: { organization: data.organization.id },
    pagination: { pageSize: 100 },
    fields: ['id', 'name'],
  });

  const breadcrumbs = useMemo<BreadCrumbItem[]>(
    () => [
      {
        label: data.name,
        to: '/chats/$chatId/edit',
        params: { chatId },
      },
      {
        label: t`Knowledge Base`,
        to: '/chats/$chatId/documents',
        params: { chatId },
      },
    ],
    [data.name, chatId, t],
  );

  return (
    <ResourcePage
      title={t`Knowledge Base`}
      breadcrumbs={breadcrumbs}
      actions={
        <div className='flex items-center gap-2'>
          <Protected roles={[Role.SuperAdmin]}>
            <RetryFailedButton
              chat={{
                id: chatId,
                parsingErrorDocumentsCount: docStats?.parsingError ?? 0,
                embeddingErrorDocumentsCount: docStats?.embeddingError ?? 0,
              }}
            />
          </Protected>
        </div>
      }
    >
      {docStats && (
        <KBStatsSummary
          stats={docStats}
          onStatusFilterChange={handleStatusFilterChange}
          onDocumentPipelineFilterChange={handleDocumentPipelineFilterChange}
          activeDocumentPipelineFilter={documentPipelineFilter}
          activeStatusFilter={statusFilter}
        />
      )}

      <KBContentsSection
        toolbar={
          <KBToolbar
            mode='chat'
            folderId={null}
            rows={contents.rows}
            selection={selection}
            mutations={mutations}
            search={search}
            onSearchChange={setSearch}
            onCreateFolder={() => {}}
            onAddDocuments={() => setShowAddDocuments(true)}
            onDeleteFolder={() => {}}
            onFolderSettings={() => {}}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            documentPipelineFilter={documentPipelineFilter}
            onDocumentPipelineFilterChange={handleDocumentPipelineFilterChange}
            sourceTypeFilter={sourceTypeFilter}
            onSourceTypeFilterChange={handleSourceTypeFilterChange}
            onBulkAgentAccess={contents.handleBulkAgentAccess}
            onBulkAutoUpdate={contents.handleBulkAutoUpdate}
            onBulkWeight={contents.handleBulkWeight}
            onExcludeFromKB={contents.handleExcludeFromKB}
            onRequestDeleteFolders={contents.requestDeleteFolders}
            onViewDocument={contents.handleSelectDocument}
            onExportDocuments={contents.handleOpenExport}
          />
        }
      >
        <AccessPropagationBanner chatId={chatId} />
        <KBContentTable
          rows={contents.rows}
          contentUpdatedSort={contents.contentUpdatedSort}
          onContentUpdatedSortChange={contents.setContentUpdatedSort}
          folderId={null}
          loading={contents.isLoading}
          selection={selection}
          selectedDocumentId={contents.selectedDocumentId}
          onSelectDocument={contents.handleSelectDocument}
          currentChatId={chatId}
          defaultTab={contents.defaultTab}
          columnActions={contents.columnActions}
          folderPaths={contents.folderPaths}
          onSearchReset={() => {
            setSearch('');
            handleStatusFilterChange(undefined);
            handleDocumentPipelineFilterChange(undefined);
            handleSourceTypeFilterChange(undefined);
          }}
          hasMore={contents.hasNextPage}
          isLoadingMore={contents.isFetchingNextPage}
          onLoadMore={() => contents.fetchNextPage()}
        />
      </KBContentsSection>

      <AddDocumentsModal
        chatId={chatId}
        allChats={allChats?.items ?? []}
        externalOpen={showAddDocuments}
        onExternalOpenChange={setShowAddDocuments}
        defaultOpen={searchParams && 'open' in searchParams}
        defaultUrl={searchParams?.open}
      />

      <Dialog.Root
        open={!!contents.testCasesDocumentId}
        onOpenChange={open => {
          if (!open) contents.setTestCasesDocumentId(null);
        }}
      >
        <GenerateTestCasesDialog
          document={
            contents.testCasesDocumentId
              ? ({ id: contents.testCasesDocumentId } as any)
              : null
          }
        />
      </Dialog.Root>

      <BulkAgentAccessModal
        open={contents.isBulkAgentAccessOpen}
        onOpenChange={contents.setIsBulkAgentAccessOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />

      <BulkAutoUpdateModal
        open={contents.isBulkAutoUpdateOpen}
        onOpenChange={contents.setIsBulkAutoUpdateOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />

      {contents.exportSelection.length > 0 && (
        <DocumentExportModal
          documents={contents.exportSelection}
          onClose={contents.handleCloseExport}
        />
      )}

      <BulkWeightModal
        open={contents.isBulkWeightOpen}
        onOpenChange={contents.setIsBulkWeightOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />

      {contents.folderDeletePrompt && (
        <DeleteFoldersModal
          open
          onOpenChange={open => {
            if (!open) {
              contents.clearFolderDeletePrompt();
            }
          }}
          folders={
            contents.folderDeletePrompt.mode === 'single'
              ? [contents.folderDeletePrompt.folder]
              : contents.folderDeletePrompt.folders
          }
          mutations={mutations}
          onDeleted={async () => {
            const onDeleted = contents.folderDeletePrompt?.onDeleted;

            contents.clearFolderDeletePrompt();
            selection.clearSelection();
            await onDeleted?.();
          }}
        />
      )}
    </ResourcePage>
  );
}
